/**
 * Large files go up in parts, straight to storage (R2 multipart).
 *
 * The file is split into equal parts (the last one shorter), and 4 parts go
 * up at a time, each with its own short-lived presigned URL from the API.
 * A part that fails is retried with backoff; while the browser is offline
 * the upload waits instead of burning its retries. Progress counts the bytes
 * of every part in flight.
 *
 * Resume: what has been uploaded is remembered in localStorage, keyed by the
 * file (name, size, last modified) and where it is going. Choosing the same
 * file again after a dropped connection or a reload asks the API which parts
 * storage already holds and sends only the rest.
 *
 * Cancel aborts the upload on the server, which drops the parts.
 */

import { debugLog } from '../utils/debugLog';

const MiB = 1024 * 1024;

/** Files this size and over go up in parts; smaller ones as one PUT. */
export const MULTIPART_THRESHOLD_BYTES = 100 * MiB;
/** R2: every part but the last is at least 5 MiB and the same size; at most 10,000 parts. */
export const MIN_PART_BYTES = 5 * MiB;
export const MAX_PARTS = 10000;
const DEFAULT_MIN_PART_BYTES = 16 * MiB;
const DEFAULT_MAX_PART_BYTES = 64 * MiB;
const DEFAULT_TARGET_PARTS = 500;
const PART_STEP_BYTES = 8 * MiB;

export const DEFAULT_CONCURRENCY = 4;
/** Tries per part while online (the waits add up to about two and a half minutes). */
export const DEFAULT_PART_ATTEMPTS = 10;
const MAX_RETRY_DELAY_MS = 30 * 1000;
/** How long to wait for the browser to come back online before giving up (the upload can still be resumed). */
const OFFLINE_WAIT_MS = 10 * 60 * 1000;
/** A part that makes no progress for this long is dropped and retried. */
const STALL_TIMEOUT_MS = 60 * 1000;
/** Part URLs are asked for in batches, and replaced this long before they expire. */
const URL_BATCH_SIZE = 8;
const URL_SAFETY_MS = 2 * 60 * 1000;
/** Resume records older than this are forgotten (the server drops unfinished uploads within 6 days). */
const RESUME_STATE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const RESUME_STORAGE_KEY = 'pithplay.multipartUploads.v1';
const MAX_RESUME_RECORDS = 20;

// --- Part planning -------------------------------------------------------------

/**
 * 16 to 64 MiB parts, in 8 MiB steps, aiming at about 500 parts at most: a
 * retried part re-sends at most 64 MiB, and a 25 GB file is about 460 parts.
 * (The API plans the same way.)
 */
export function defaultPartSizeBytes(fileSize: number): number {
  const size = Number.isFinite(fileSize) && fileSize > 0 ? fileSize : 0;
  const target = Math.ceil(size / DEFAULT_TARGET_PARTS / PART_STEP_BYTES) * PART_STEP_BYTES;
  let partSize = Math.min(DEFAULT_MAX_PART_BYTES, Math.max(DEFAULT_MIN_PART_BYTES, target));
  if (Math.ceil(size / partSize) > MAX_PARTS) partSize = Math.ceil(size / MAX_PARTS / MiB) * MiB;
  return partSize;
}

export const partCountFor = (fileSize: number, partSize: number): number => Math.max(1, Math.ceil(fileSize / partSize));

/** Byte range [start, end) of part `partNumber` (1-based). */
export function partRange(partNumber: number, partSize: number, fileSize: number): { start: number; end: number } {
  const start = (partNumber - 1) * partSize;
  return { start, end: Math.min(fileSize, start + partSize) };
}

// --- What the engine needs from the API --------------------------------------------

export interface MultipartTarget {
  objectKey: string;
  uploadId: string;
  partSizeBytes: number;
  partCount: number;
}

export interface PartUrl {
  partNumber: number;
  url: string;
  headers: Array<{ name: string; value: string }>;
}

export interface PartUrlBatch {
  parts: PartUrl[];
  expiresInSeconds: number;
}

export interface MultipartResumeState extends MultipartTarget {
  uploadedParts: number[];
  uploadedBytes: number;
  /** UPLOADING, FINISHING (being completed) or UPLOADED (complete it to get the result). */
  status: string;
}

export interface MultipartUploadApi<T> {
  start(partSizeBytes: number): Promise<MultipartTarget>;
  signParts(target: MultipartTarget, partNumbers: number[]): Promise<PartUrlBatch>;
  resume(target: MultipartTarget): Promise<MultipartResumeState>;
  complete(target: MultipartTarget): Promise<T>;
  abort(target: MultipartTarget): Promise<boolean>;
}

/**
 * An API failure. `code` and `reason` come from the GraphQL error's
 * extensions; `network` means the request never got an answer; `status` is
 * the HTTP status when there was one.
 */
export class UploadApiError extends Error {
  code?: string;
  reason?: string;
  extensions: Record<string, unknown>;
  status?: number;
  network: boolean;

  constructor(
    message: string,
    details: { code?: string; reason?: string; extensions?: Record<string, unknown>; status?: number; network?: boolean } = {},
  ) {
    super(message);
    this.name = 'UploadApiError';
    this.code = details.code;
    this.reason = details.reason;
    this.extensions = details.extensions || {};
    this.status = details.status;
    this.network = Boolean(details.network);
  }
}

/** A part's PUT to storage failed: `status` 0 means no answer (dropped, offline, stalled). */
export class PartUploadError extends Error {
  status: number;

  constructor(status: number, message?: string) {
    super(message || (status ? `Part upload failed (${status})` : 'Part upload got no answer'));
    this.name = 'PartUploadError';
    this.status = status;
  }
}

/** The upload stopped. With `resumable`, choosing the same file again continues it. */
export class MultipartUploadError extends Error {
  resumable: boolean;

  constructor(message: string, resumable: boolean) {
    super(message);
    this.name = 'MultipartUploadError';
    this.resumable = resumable;
  }
}

const RETRYABLE_API_CODES = ['STORAGE_UNAVAILABLE', 'UPLOAD_FINISHING'];

export function isRetryableApiError(error: unknown): boolean {
  if (!(error instanceof UploadApiError)) return false;
  if (error.network) return true;
  if (error.status !== undefined && (error.status === 429 || error.status >= 500)) return true;
  return Boolean(error.code && RETRYABLE_API_CODES.indexOf(error.code) >= 0);
}

const isExpiredError = (error: unknown) => error instanceof UploadApiError && error.code === 'UPLOAD_EXPIRED';
const isSizeMismatch = (error: unknown) => error instanceof UploadApiError && error.reason === 'SIZE_MISMATCH';

export const abortError = (): Error => {
  try {
    return new DOMException('Upload cancelled', 'AbortError');
  } catch {
    const error = new Error('Upload cancelled');
    error.name = 'AbortError';
    return error;
  }
};

const isAbortError = (error: unknown) => (error as { name?: string } | null)?.name === 'AbortError';

// --- Sending one part ----------------------------------------------------------------

export interface SendPartOptions {
  /** Bytes of this part sent so far. */
  onProgress(loaded: number): void;
  signal: AbortSignal;
  stallTimeoutMs: number;
}

export type PartSender = (part: PartUrl, body: Blob, options: SendPartOptions) => Promise<void>;

/**
 * PUTs one part with XMLHttpRequest (fetch can't report upload progress).
 * Rejects with PartUploadError, or AbortError when cancelled. A part that
 * makes no progress for `stallTimeoutMs` is dropped, so a connection that
 * died quietly is noticed, and so is one in flight when the browser goes
 * offline (some browsers hold such a request instead of failing it).
 */
export const sendPartWithXhr: PartSender = (part, body, { onProgress, signal, stallTimeoutMs }) =>
  new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(abortError());
      return;
    }
    const request = new XMLHttpRequest();
    let lastActivity = Date.now();
    let stalled = false;
    let settled = false;
    const handleAbort = () => request.abort();
    const handleOffline = () => {
      stalled = true;
      request.abort();
    };
    const watchdog = setInterval(() => {
      if (Date.now() - lastActivity > stallTimeoutMs) {
        stalled = true;
        request.abort();
      }
    }, Math.min(5000, Math.max(250, Math.floor(stallTimeoutMs / 4))));
    const settle = (outcome: () => void) => {
      if (settled) return;
      settled = true;
      clearInterval(watchdog);
      signal.removeEventListener('abort', handleAbort);
      if (typeof window !== 'undefined') window.removeEventListener('offline', handleOffline);
      outcome();
    };
    request.open('PUT', part.url);
    part.headers.forEach(({ name, value }) => request.setRequestHeader(name, value));
    request.upload.onprogress = (event) => {
      lastActivity = Date.now();
      if (event.lengthComputable) onProgress(event.loaded);
    };
    request.onload = () =>
      settle(() => {
        if (request.status >= 200 && request.status < 300) resolve();
        else reject(new PartUploadError(request.status));
      });
    request.onerror = () => settle(() => reject(new PartUploadError(0)));
    request.ontimeout = () => settle(() => reject(new PartUploadError(0, 'Part upload timed out')));
    request.onabort = () =>
      settle(() => reject(stalled ? new PartUploadError(0, 'Part upload stalled') : abortError()));
    signal.addEventListener('abort', handleAbort, { once: true });
    if (typeof window !== 'undefined') window.addEventListener('offline', handleOffline);
    request.send(body);
  });

// --- Remembering uploads for resume ---------------------------------------------------

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface SavedUpload extends MultipartTarget {
  fingerprint: string;
  partsDone: number[];
  savedAt: number;
}

/** The file and where it's going: same name, size and last-modified time means the same file. */
export function fileFingerprint(scope: string, file: { name: string; size: number; lastModified?: number }): string {
  return [scope, file.name, file.size, file.lastModified || 0].join('|');
}

function defaultStorage(): StorageLike | null {
  try {
    return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null;
  } catch {
    return null; // blocked storage, private windows
  }
}

/** Every access is wrapped: storage can be missing, full or blocked, and the upload must work without it. */
export function resumeStore(storage: StorageLike | null, now: () => number = Date.now) {
  const readAll = (): Record<string, SavedUpload> => {
    if (!storage) return {};
    try {
      const raw = storage.getItem(RESUME_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  };
  const writeAll = (records: Record<string, SavedUpload>) => {
    if (!storage) return;
    const fresh = Object.keys(records)
      .map((key) => records[key])
      .filter((record) => record && now() - Number(record.savedAt) < RESUME_STATE_MAX_AGE_MS)
      .sort((a, b) => b.savedAt - a.savedAt)
      .slice(0, MAX_RESUME_RECORDS);
    const kept: Record<string, SavedUpload> = {};
    fresh.forEach((record) => {
      kept[record.fingerprint] = record;
    });
    try {
      if (fresh.length) storage.setItem(RESUME_STORAGE_KEY, JSON.stringify(kept));
      else storage.removeItem(RESUME_STORAGE_KEY);
    } catch {
      // Full or blocked: the upload carries on, it just can't be resumed.
    }
  };
  return {
    load(fingerprint: string): SavedUpload | null {
      const record = readAll()[fingerprint];
      if (!record || now() - Number(record.savedAt) >= RESUME_STATE_MAX_AGE_MS) return null;
      if (typeof record.objectKey !== 'string' || typeof record.uploadId !== 'string') return null;
      if (!(record.partSizeBytes > 0) || !(record.partCount > 0)) return null;
      return { ...record, partsDone: Array.isArray(record.partsDone) ? record.partsDone : [] };
    },
    save(record: Omit<SavedUpload, 'savedAt'>): void {
      const records = readAll();
      records[record.fingerprint] = { ...record, savedAt: now() };
      writeAll(records);
    },
    remove(fingerprint: string): void {
      const records = readAll();
      if (!records[fingerprint]) return;
      delete records[fingerprint];
      writeAll(records);
    },
  };
}

// --- Waiting ------------------------------------------------------------------------

function sleepFor(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError());
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(abortError());
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

const browserIsOnline = () => (typeof navigator === 'undefined' || navigator.onLine !== false);

/** Resolves when the browser is back online, or after `maxMs`. */
function waitUntilOnline(signal: AbortSignal | undefined, maxMs: number): Promise<void> {
  return new Promise((resolve, reject) => {
    if (browserIsOnline()) {
      resolve();
      return;
    }
    const finish = (outcome: () => void) => {
      clearTimeout(timer);
      window.removeEventListener('online', onOnline);
      signal?.removeEventListener('abort', onAbort);
      outcome();
    };
    const onOnline = () => finish(resolve);
    const onAbort = () => finish(() => reject(abortError()));
    const timer = setTimeout(() => finish(resolve), maxMs);
    window.addEventListener('online', onOnline);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/** 1 s, 2 s, 4 s … up to 30 s, each with up to half of it added at random. */
export function retryDelayMs(attempt: number, random: () => number = Math.random): number {
  const base = Math.min(MAX_RETRY_DELAY_MS, 1000 * Math.pow(2, Math.max(0, attempt - 1)));
  return Math.round(base * (0.5 + random() * 0.5));
}

// --- The upload ----------------------------------------------------------------------

export interface UploadInPartsOptions {
  /** Where the file goes, for resuming: e.g. "asset:original-video", "revision:<project id>". */
  scope: string;
  /** 0-99 while parts go up, 100 once the upload is complete. */
  onProgress?: (percent: number) => void;
  /** Told when an earlier upload of this file is being continued. */
  onResume?: (info: { uploadedBytes: number; totalBytes: number }) => void;
  signal?: AbortSignal;
  /** A part size for new uploads (the API's rules apply); the default suits the file. */
  partSizeBytes?: number;
  concurrency?: number;
  maxAttempts?: number;
  stallTimeoutMs?: number;
  // For tests:
  sendPart?: PartSender;
  storage?: StorageLike | null;
  sleep?: (ms: number, signal?: AbortSignal) => Promise<void>;
  now?: () => number;
  random?: () => number;
  isOnline?: () => boolean;
  waitForOnline?: (signal: AbortSignal | undefined, maxMs: number) => Promise<void>;
}

type UploadFile = Blob & { name: string; lastModified?: number };

const percentOf = (done: number, total: number) => (total > 0 ? Math.min(99, Math.floor((done / total) * 100)) : 0);

/** Uploads `file` in parts through `api`, resuming an earlier upload of the same file. Resolves with what `api.complete` gives. */
export async function uploadInParts<T>(file: UploadFile, api: MultipartUploadApi<T>, options: UploadInPartsOptions): Promise<T> {
  const total = file.size;
  const now = options.now || Date.now;
  const random = options.random || Math.random;
  const sleep = options.sleep || sleepFor;
  const isOnline = options.isOnline || browserIsOnline;
  const waitForOnline = options.waitForOnline || waitUntilOnline;
  const sendPart = options.sendPart || sendPartWithXhr;
  const concurrency = Math.max(1, Math.floor(options.concurrency || DEFAULT_CONCURRENCY));
  const maxAttempts = Math.max(1, Math.floor(options.maxAttempts || DEFAULT_PART_ATTEMPTS));
  const stallTimeoutMs = options.stallTimeoutMs || STALL_TIMEOUT_MS;
  const store = resumeStore(options.storage === undefined ? defaultStorage() : options.storage, now);
  const fingerprint = fileFingerprint(options.scope, file);
  const userSignal = options.signal;

  // One controller for everything this upload does; the caller's signal aborts it.
  const controller = new AbortController();
  const signal = controller.signal;
  const forwardAbort = () => controller.abort();
  if (userSignal?.aborted) throw abortError();
  userSignal?.addEventListener('abort', forwardAbort, { once: true });

  let lastPercent = -1;
  const report = (percent: number) => {
    if (percent !== lastPercent) {
      lastPercent = percent;
      options.onProgress?.(percent);
    }
  };

  /** Retries an API call while it fails for reasons that pass (network, storage hiccups, a completion in progress). */
  const callApi = async <R>(call: () => Promise<R>, attempts = 6): Promise<R> => {
    for (let attempt = 1; ; attempt += 1) {
      if (signal.aborted) throw abortError();
      try {
        return await call();
      } catch (error) {
        if (signal.aborted || isAbortError(error)) throw abortError();
        if (!isRetryableApiError(error) || attempt >= attempts) throw error;
        if (!isOnline()) await waitForOnline(signal, OFFLINE_WAIT_MS);
        await sleep(retryDelayMs(attempt, random), signal);
      }
    }
  };

  let target: MultipartTarget | null = null;
  let partsDone: number[] = [];
  let alreadyComplete = false;

  const remember = () => {
    if (!target) return;
    store.save({ fingerprint, objectKey: target.objectKey, uploadId: target.uploadId, partSizeBytes: target.partSizeBytes, partCount: target.partCount, partsDone });
  };

  try {
    // 1. Continue an earlier upload of this file, or start one.
    const saved = store.load(fingerprint);
    if (saved && saved.partCount === partCountFor(total, saved.partSizeBytes)) {
      try {
        const state = await callApi(() => api.resume(saved));
        if (state.partSizeBytes !== saved.partSizeBytes || state.partCount !== partCountFor(total, state.partSizeBytes)) {
          throw new UploadApiError('The saved upload is for a different file', { code: 'UPLOAD_EXPIRED' });
        }
        target = { objectKey: state.objectKey, uploadId: state.uploadId, partSizeBytes: state.partSizeBytes, partCount: state.partCount };
        alreadyComplete = state.status === 'UPLOADED' || state.status === 'FINISHING';
        partsDone = alreadyComplete
          ? Array.from({ length: target.partCount }, (_unused, index) => index + 1)
          : state.uploadedParts.filter((n) => n >= 1 && n <= state.partCount);
        debugLog('Resuming a multipart upload', { objectKey: target.objectKey, parts: partsDone.length, of: target.partCount });
        if (state.uploadedBytes > 0) options.onResume?.({ uploadedBytes: Math.min(total, state.uploadedBytes), totalBytes: total });
      } catch (error) {
        if (!isExpiredError(error)) throw error;
        store.remove(fingerprint);
        target = null;
      }
    } else if (saved) {
      store.remove(fingerprint);
    }

    if (!target) {
      report(0);
      const partSizeBytes = options.partSizeBytes || defaultPartSizeBytes(total);
      // Not retried: a lost answer could otherwise start two uploads.
      target = await api.start(partSizeBytes);
      if (signal.aborted) throw abortError();
      partsDone = [];
    }
    remember();
    const upload: MultipartTarget = target;

    // 2. Send the parts storage doesn't have yet.
    const partBytes = (partNumber: number) => {
      const { start, end } = partRange(partNumber, upload.partSizeBytes, total);
      return end - start;
    };
    let confirmedBytes = 0;
    const done: Record<number, true> = {};
    partsDone.forEach((partNumber) => {
      if (!done[partNumber]) {
        done[partNumber] = true;
        confirmedBytes += partBytes(partNumber);
      }
    });
    const inFlight: Record<number, number> = {};
    const reportProgress = () => {
      let sending = 0;
      Object.keys(inFlight).forEach((key) => {
        sending += inFlight[Number(key)];
      });
      report(percentOf(confirmedBytes + sending, total));
    };
    reportProgress();

    const urls = new PartUrlPool(upload, api, callApi, now);

    const sendWithRetries = async (partNumber: number, queue: number[]) => {
      let attempt = 0;
      let freshUrls = 0;
      for (;;) {
        if (signal.aborted) throw abortError();
        // Don't start a part while offline: wait for the connection first.
        if (!isOnline()) await waitForOnline(signal, OFFLINE_WAIT_MS);
        const url = await urls.get(partNumber, queue);
        const { start, end } = partRange(partNumber, upload.partSizeBytes, total);
        try {
          await sendPart(url, file.slice(start, end), {
            signal,
            stallTimeoutMs,
            onProgress: (loaded) => {
              inFlight[partNumber] = Math.min(loaded, end - start);
              reportProgress();
            },
          });
          return;
        } catch (error) {
          delete inFlight[partNumber];
          reportProgress();
          if (signal.aborted || isAbortError(error)) throw abortError();
          const status = error instanceof PartUploadError ? error.status : 0;
          if (status === 403 && freshUrls < 3) {
            // The URL ran out (a slow part, a sleeping laptop): get a new one.
            freshUrls += 1;
            urls.forget(partNumber);
            continue;
          }
          if (status === 404) throw new UploadApiError('The upload is gone', { code: 'UPLOAD_EXPIRED' });
          if (!isOnline()) {
            // Offline doesn't use up tries: wait for the connection.
            await waitForOnline(signal, OFFLINE_WAIT_MS);
            if (!isOnline()) throw error;
            continue;
          }
          attempt += 1;
          if (attempt >= maxAttempts) throw error;
          debugLog('Retrying part', partNumber, 'after', error);
          await sleep(retryDelayMs(attempt, random), signal);
        }
      }
    };

    const sendParts = async (partNumbers: number[]) => {
      const queue = partNumbers.filter((n) => !done[n]);
      let failure: unknown = null;
      const worker = async () => {
        while (queue.length > 0 && failure === null) {
          const partNumber = queue.shift() as number;
          try {
            await sendWithRetries(partNumber, queue);
          } catch (error) {
            if (failure === null) failure = error;
            controller.abort(); // stop the other parts too
            return;
          }
          delete inFlight[partNumber];
          if (!done[partNumber]) {
            done[partNumber] = true;
            confirmedBytes += partBytes(partNumber);
            partsDone.push(partNumber);
          }
          remember();
          reportProgress();
        }
      };
      const workers: Promise<void>[] = [];
      for (let i = 0; i < Math.min(concurrency, queue.length); i += 1) workers.push(worker());
      await Promise.all(workers);
      if (failure !== null) throw failure;
    };

    if (!alreadyComplete) {
      await sendParts(Array.from({ length: upload.partCount }, (_unused, index) => index + 1));
    }

    // 3. Complete. Parts storage says are missing go up again (once).
    const missingParts = (error: unknown): number[] => {
      const listed = error instanceof UploadApiError && error.reason === 'PARTS_MISSING' ? error.extensions.missingParts : null;
      return Array.isArray(listed)
        ? listed.filter((n): n is number => typeof n === 'number' && n >= 1 && n <= upload.partCount && Math.floor(n) === n)
        : [];
    };
    const sendAgain = async (missing: number[]) => {
      missing.forEach((partNumber) => {
        if (done[partNumber]) {
          delete done[partNumber];
          confirmedBytes -= partBytes(partNumber);
        }
      });
      partsDone = partsDone.filter((partNumber) => missing.indexOf(partNumber) < 0);
      remember();
      reportProgress();
      await sendParts(missing);
    };
    let result: T | null = null;
    let finished = false;
    for (let round = 0; !finished; round += 1) {
      try {
        result = await callApi(() => api.complete(upload), 8);
        finished = true;
      } catch (error) {
        const missing = missingParts(error);
        if (missing.length === 0 || round >= 1) throw error;
        await sendAgain(missing);
      }
    }
    store.remove(fingerprint);
    report(100);
    return result as T;
  } catch (error) {
    if (userSignal?.aborted || isAbortError(error)) {
      // Cancelled: drop the parts on the server (best effort) and forget the upload.
      store.remove(fingerprint);
      if (target) {
        const cancelled = target;
        api.abort(cancelled).catch(() => undefined);
      }
      throw abortError();
    }
    if (isExpiredError(error) || isSizeMismatch(error)) {
      store.remove(fingerprint);
      throw new MultipartUploadError(
        isSizeMismatch(error)
          ? (error as Error).message
          : 'This upload expired before it finished. Choose the file again to start it over.',
        false,
      );
    }
    if (error instanceof PartUploadError || (error instanceof UploadApiError && isRetryableApiError(error))) {
      // The parts that made it stay on the server, and this browser remembers them.
      const percent = Math.max(0, lastPercent);
      throw new MultipartUploadError(
        percent > 0
          ? `The upload stopped at ${percent}% because the connection dropped. Choose the same file again to continue where it stopped.`
          : 'The upload stopped because the connection dropped. Choose the same file again to try once more.',
        true,
      );
    }
    throw error;
  } finally {
    userSignal?.removeEventListener('abort', forwardAbort);
  }
}

/**
 * Presigned part URLs: asked for in batches (the part needed now and the
 * next ones in line), kept until shortly before they expire, and asked for
 * again after that or when storage refuses one. Validity is measured on this
 * device's clock from when the URLs arrived, so a wrong clock can't make every
 * URL look expired.
 */
class PartUrlPool<T> {
  private urls: Record<number, { part: PartUrl; validUntil: number }> = {};
  private loading: Promise<void> | null = null;

  constructor(
    private readonly target: MultipartTarget,
    private readonly api: MultipartUploadApi<T>,
    private readonly callApi: <R>(call: () => Promise<R>, attempts?: number) => Promise<R>,
    private readonly now: () => number,
  ) {}

  private valid(partNumber: number): PartUrl | null {
    const entry = this.urls[partNumber];
    return entry && entry.validUntil - this.now() > URL_SAFETY_MS ? entry.part : null;
  }

  forget(partNumber: number): void {
    delete this.urls[partNumber];
  }

  async get(partNumber: number, upcoming: number[]): Promise<PartUrl> {
    for (let tries = 0; tries < 4; tries += 1) {
      const cached = this.valid(partNumber);
      if (cached) return cached;
      if (this.loading) {
        try {
          await this.loading;
        } catch {
          // The other request failed; try our own below.
        }
        continue;
      }
      const wanted = [partNumber].concat(upcoming.filter((n) => n !== partNumber && !this.valid(n))).slice(0, URL_BATCH_SIZE);
      const load = this.load(wanted);
      this.loading = load;
      try {
        await load;
      } finally {
        if (this.loading === load) this.loading = null;
      }
    }
    const last = this.valid(partNumber);
    if (last) return last;
    throw new UploadApiError('No upload URL came back for a part', { code: 'STORAGE_UNAVAILABLE' });
  }

  private async load(partNumbers: number[]): Promise<void> {
    const askedAt = this.now();
    const batch = await this.callApi(() => this.api.signParts(this.target, partNumbers));
    const lifetimeMs = Math.max(0, Number(batch.expiresInSeconds) || 0) * 1000;
    batch.parts.forEach((part) => {
      this.urls[part.partNumber] = { part, validUntil: askedAt + lifetimeMs };
    });
  }
}
